import { color, font, icon, stroke, testColors } from "@mesketch/design-tokens";
import type { ReactElement } from "react";

// Obraz výsledku testů s pořadím (CR-014 §3, zadání 8.5.2): nejsilnější silné stránky jako hvězdy
// (příprava na vlastní souhvězdí) a nejsilnější oblasti práce jako cesta po krocích s rostoucí
// výškou. Bez čísel skóre (CON-002), velikost a výška jen podle místa v pořadí. Mezi koly tentýž
// obraz tužkou bez popisků, názvy jsou v „Zatím vedou“ (Libor 19. 10. 2026).

export interface RankedName {
  readonly id: string;
  /** Místo v pořadí, sdílené při shodě (CR-012). */
  readonly place: number;
  readonly name: string;
}

const STAR = "20,3 24.7,13.5 36.2,14.8 27.6,22.5 30,33.8 20,28 10,33.8 12.4,22.5 3.8,14.8 15.3,13.5";

/** Barevně na Skice, tužkou mezi koly. */
export type OrderVariant = "color" | "pencil";

/** Druhý tah tužky lehce posunutý (jako u kresby typu). */
function pencilEcho(shape: ReactElement) {
  return (
    <g transform="translate(1.4 -1.2)" stroke={color.pencilLine} strokeWidth={stroke.pencilSecond} opacity={stroke.pencilSecondOpacity} fill="none">
      {shape}
    </g>
  );
}

const pencilLine = { fill: "none", stroke: color.pencilLine, strokeWidth: stroke.pencil, strokeLinejoin: "round" as const };

// Souhvězdí od středu (Libor 19. 10. 2026): nejsilnější uprostřed a největší, další místa v kruzích
// kolem a výrazně menší. Spojnice vedou od středu ven, takže se nekříží a je vidět, co je hlavní.
const SKY_W = 560;
const SKY_H = 290;
const CX = SKY_W / 2;
const CY = 140;
/** Kruhy kolem středu: kolik hvězd pojmou a jejich poloosy. */
const RINGS = [
  { n: 1, rx: 0, ry: 0 },
  { n: 6, rx: 130, ry: 70 },
  { n: 10, rx: 228, ry: 114 },
] as const;
/** Poloměr hvězdy podle pořadí jejího místa mezi různými místy (první, druhé, třetí, další). */
const RADII = [36, 24, 17, 12] as const;

export interface StarSpot {
  readonly star: RankedName;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  /** 0 střed, 1 vnitřní kruh, 2 vnější kruh. */
  readonly ring: number;
  /** Odkud vede spojnice (střed nebo nejbližší hvězda vnitřního kruhu), u středu null. */
  readonly from: readonly [number, number] | null;
}

/** Rozmístění hvězd v pořadí místa: střed, pak vnitřní a vnější kruh. Víc hvězd, než pojmou, se nekreslí. */
export function starLayout(stars: readonly RankedName[]): StarSpot[] {
  const places = [...new Set(stars.map((s) => s.place))].sort((a, b) => a - b);
  const radius = (place: number): number => RADII[Math.min(places.indexOf(place), RADII.length - 1)] ?? 11;
  const out: StarSpot[] = [];
  const inner: { angle: number; x: number; y: number }[] = [];
  let next = 0;
  const innerCount = Math.max(1, Math.min(RINGS[1].n, stars.length - 1));
  RINGS.forEach((ring, k) => {
    const members = stars.slice(next, next + ring.n);
    next += ring.n;
    members.forEach((star, j) => {
      // Vnější kruh začíná v mezeře mezi hvězdami vnitřního, aby žádná nebyla přímo pod jinou.
      const angle = -Math.PI / 2 + (j * 2 * Math.PI) / members.length + (k === 2 ? Math.PI / innerCount : 0);
      const x = Math.round(CX + ring.rx * Math.cos(angle));
      const y = Math.round(CY + ring.ry * Math.sin(angle));
      let from: readonly [number, number] | null = null;
      if (k === 1) {
        from = [CX, CY];
        inner.push({ angle, x, y });
      } else if (k === 2) {
        const gap = (a: number) => Math.abs(Math.atan2(Math.sin(a - angle), Math.cos(a - angle)));
        const near = inner.reduce<(typeof inner)[number] | null>((best, p) => (best === null || gap(p.angle) < gap(best.angle) ? p : best), null);
        from = near === null ? [CX, CY] : [near.x, near.y];
      }
      out.push({ star, x, y, r: radius(star.place), ring: k, from });
    });
  });
  return out;
}

export function Stars({ stars, label, variant = "color" }: { stars: readonly RankedName[]; label: string; variant?: OrderVariant }) {
  const pencil = variant === "pencil";
  const spots = starLayout(stars);
  return (
    <svg viewBox={`0 0 ${SKY_W} ${SKY_H}`} width="100%" role="img" aria-label={label} data-testid="stars" data-variant={variant} style={{ display: "block" }}>
      {!pencil ? (
        <defs>
          <radialGradient id="star-glow">
            <stop offset="0%" stopColor={testColors.strength.main} stopOpacity="0.3" />
            <stop offset="100%" stopColor={testColors.strength.main} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="star-fill">
            <stop offset="0%" stopColor={icon.strengthInner} />
            <stop offset="80%" stopColor={testColors.strength.main} />
          </radialGradient>
        </defs>
      ) : null}
      {spots.map((p) =>
        p.from === null ? null : (
          <line key={`l-${p.star.id}`} x1={p.from[0]} y1={p.from[1]} x2={p.x} y2={p.y} stroke={pencil ? color.pencilLine : testColors.strength.bg} strokeWidth={pencil ? "1.1" : "1.5"} strokeDasharray={pencil ? "3 4" : "2 5"} strokeLinecap="round" />
        ),
      )}
      {spots.map((p) => {
        const k = p.r / 17;
        const shape = <polygon points={STAR} />;
        return (
          <g key={p.star.id} data-star={p.star.id} data-place={p.star.place} data-ring={p.ring}>
            {pencil ? (
              <g transform={`translate(${p.x} ${p.y}) scale(${k}) translate(-20 -20)`} {...pencilLine} strokeWidth={(stroke.pencil * 17) / p.r}>
                {shape}
                {pencilEcho(shape)}
              </g>
            ) : (
              <>
                {/* Záře kolem hvězdy — větší u prvního místa. */}
                <circle cx={p.x} cy={p.y} r={p.r * 1.8} fill="url(#star-glow)" />
                <g transform={`translate(${p.x} ${p.y}) scale(${k}) translate(-20 -20)`}>
                  <polygon points={STAR} fill="url(#star-fill)" stroke={testColors.strength.main} strokeWidth="2" strokeLinejoin="round" />
                  <g transform="translate(20 19.6) scale(0.38) translate(-20 -20)">
                    <polygon points={STAR} fill={icon.strengthInner} opacity="0.6" />
                  </g>
                </g>
                <text x={p.x} y={p.y + p.r + 16} textAnchor="middle" fontSize={p.ring === 0 ? 15 : 12.5} fontWeight={p.ring === 0 ? 700 : 500} fill={color.ink} fontFamily={font.text}>
                  {p.star.name}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function Steps({ steps, label, variant = "color" }: { steps: readonly RankedName[]; label: string; variant?: OrderVariant }) {
  if (steps.length === 0) return null;
  const pencil = variant === "pencil";
  const w = 480;
  const h = 220;
  const gap = 10;
  // Zleva nejnižší: pořadí obrácené, nejsilnější vpravo nejvýš. Shodné místo = stejná výška.
  const ordered = [...steps].reverse();
  const places = [...new Set(steps.map((s) => s.place))].sort((a, b) => b - a);
  const level = (place: number) => places.indexOf(place);
  const stepW = (w - gap * (ordered.length - 1)) / ordered.length;
  const heightOf = (place: number) => 52 + level(place) * (places.length > 1 ? 88 / (places.length - 1) : 0);
  const tops = ordered.map((s, i) => ({ x: i * (stepW + gap) + stepW / 2, y: h - heightOf(s.place) }));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label={label} data-testid="steps" data-variant={variant} style={{ display: "block" }}>
      {ordered.map((s, i) => {
        const sh = heightOf(s.place);
        const x = i * (stepW + gap);
        const rect = <rect x={x} y={h - sh} width={stepW} height={sh} rx="8" />;
        return (
          <g key={s.id} data-step={s.id} data-place={s.place}>
            {pencil ? (
              <g {...pencilLine}>
                {rect}
                {pencilEcho(rect)}
              </g>
            ) : (
              <>
                <rect x={x} y={h - sh} width={stepW} height={sh} rx="8" fill={testColors.work.bg} stroke={testColors.work.strong} strokeWidth="1.4" />
                <text x={x + stepW / 2} y={h - sh - 10} textAnchor="middle" fontSize="13" fontWeight="600" fill={color.ink} fontFamily={font.text}>
                  {s.name}
                </text>
              </>
            )}
          </g>
        );
      })}
      <polyline
        points={tops.map((t) => `${t.x},${t.y + 18}`).join(" ")}
        fill="none"
        stroke={pencil ? color.pencilLine : testColors.work.main}
        strokeWidth={pencil ? "1.4" : "2"}
        strokeDasharray="1 6"
        strokeLinecap="round"
      />
    </svg>
  );
}
